/** Public configuration only. Provider secrets and verification stay on the API. */
export interface OtpConfiguration {
  firebase?: { apiKey: string; authDomain: string; projectId: string };
  requiresRecaptcha?: boolean;
  fallbackAfterSeconds: number;
}

export interface OtpChallenge {
  challengeId: string;
  provider: "firebase" | "supabase" | "whatsapp" | "sms" | "sandbox";
  expiresInSeconds: number;
  fallbackAfterSeconds: number | null;
  phoneMasked?: string;
  devCode?: string;
}

export interface OtpProof { challengeId: string; code: string }

/** The staff API's adaptive proof-of-work check, shared by admin and counter sign-in. */
export async function solveLoginSecurityCheck(nonce: string, bits: number, onProgress: (value: number) => void, signal?: AbortSignal): Promise<string> {
  if (!nonce || nonce.length > 200 || !Number.isInteger(bits) || bits < 0 || bits > 24) throw new Error("Invalid security check. Please retry sign-in.");
  const encoder = new TextEncoder();
  const expected = 2 ** bits;
  for (let counter = 0; ; counter++) {
    signal?.throwIfAborted();
    const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", encoder.encode(`${nonce}:${counter}`)));
    let zeros = 0;
    for (const byte of digest) {
      if (byte === 0) { zeros += 8; continue; }
      zeros += Math.clz32(byte) - 24; break;
    }
    if (zeros >= bits) return String(counter);
    if (counter % 512 === 0) onProgress(Math.min(0.97, counter / expected));
  }
}

/** Let the server decide whether this operation needs Firebase's web security check (trusted devices do not). */
export async function requestWithOtpSecurity<T>(operation: (recaptchaToken?: string) => Promise<T>, signal?: AbortSignal): Promise<T> {
  signal?.throwIfAborted();
  try { return await operation(); }
  catch (failure) {
    if (!failure || typeof failure !== "object" || !("code" in failure) || failure.code !== "OTP_CAPTCHA") throw failure;
    const token = await otpRecaptchaToken(signal);
    signal?.throwIfAborted();
    if (!token) throw failure;
    return operation(token);
  }
}

/** Obtain the short-lived web app verification token; never sign the browser into Firebase. */
export async function otpRecaptchaToken(signal?: AbortSignal): Promise<string | undefined> {
  signal?.throwIfAborted();
  const response = await fetch("/api/public/otp/config", { credentials: "same-origin", signal: signal ? AbortSignal.any([signal, AbortSignal.timeout(10_000)]) : AbortSignal.timeout(10_000) });
  if (!response.ok) throw new Error("Cannot load mobile verification. Please try again.");
  const configuration = await response.json() as OtpConfiguration;
  if (!configuration.firebase) return undefined;
  const [{ getApps, initializeApp }, { getAuth, RecaptchaVerifier }] = await Promise.all([import("firebase/app"), import("firebase/auth")]);
  signal?.throwIfAborted();
  const name = `temple-otp-${configuration.firebase.projectId}`;
  const app = getApps().find((candidate) => candidate.name === name) ?? initializeApp(configuration.firebase, name);
  const container = document.createElement("div");
  container.setAttribute("aria-label", "Mobile verification security check");
  document.body.append(container);
  let verifier: InstanceType<typeof RecaptchaVerifier> | undefined;
  let timeout: ReturnType<typeof setTimeout> | undefined;
  let abort: (() => void) | undefined;
  try {
    verifier = new RecaptchaVerifier(getAuth(app), container, { size: "invisible" });
    return await Promise.race([
      verifier.verify(),
      new Promise<never>((_, reject) => { timeout = setTimeout(() => reject(new Error("The security check timed out. Please try again.")), 120_000); }),
      new Promise<never>((_, reject) => {
        abort = () => reject(new DOMException("Verification cancelled", "AbortError"));
        signal?.addEventListener("abort", abort, { once: true });
      }),
    ]);
  } finally {
    clearTimeout(timeout);
    if (abort) signal?.removeEventListener("abort", abort);
    verifier?.clear();
    container.remove();
  }
}

/** Validate an OTP_REQUIRED error's public details before placing it in the UI. */
export function otpChallengeFrom(value: unknown): OtpChallenge | null {
  if (!value || typeof value !== "object") return null;
  const challenge = value as Partial<OtpChallenge>;
  return typeof challenge.challengeId === "string" && typeof challenge.provider === "string"
    && ["firebase", "supabase", "whatsapp", "sms", "sandbox"].includes(challenge.provider)
    && typeof challenge.expiresInSeconds === "number" ? challenge as OtpChallenge : null;
}

export function otpChannel(challenge: OtpChallenge): "whatsapp" | "sms" | "sandbox" {
  return challenge.provider === "whatsapp" ? "whatsapp" : challenge.provider === "sandbox" ? "sandbox" : "sms";
}
